import React, {
  ReactNode, useEffect, useMemo, useRef, useState,
} from 'react';
import ReactDOM from 'react-dom';
import { unescape } from 'lodash';
import HTMLReactParserSafe from '../../partials/html-react-parser-safe';
import useWindowDimensions from '../../../hooks/useWindowDimensions';

interface Props {
  lines: number;
  className?: string;
  readMoreText?: ReactNode;
  readLessText?: ReactNode;
}

const AutoReadMore: React.FC<Props> = ({
  lines,
  className = '',
  readMoreText = <>read more</>,
  readLessText = <>read less</>,
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const mainContainerRef = useRef<HTMLDivElement>(null);
  const textContainerRef = useRef<HTMLDivElement>(null);
  const buttonContainerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { width } = useWindowDimensions();

  useEffect(() => {
    if (mainContainerRef.current
      && textContainerRef.current
      && buttonContainerRef.current
      && buttonRef.current
    ) {
      const lineHeight = parseFloat(window.getComputedStyle(textContainerRef.current).lineHeight);
      // clientHeight includes padding, but padding is set to 0 via CSS so it can be ignored
      const numLines = Math.ceil(textContainerRef.current.clientHeight / lineHeight);

      // Set the bounding box of the button container to only be the height of a single line. This
      // allows the text to wrap over top of the read more button
      // @ts-ignore TODO: LPR-793 upgrade to typescript v5 to remove this error
      buttonContainerRef.current.style.shapeOutside = `inset(calc(100% - ${buttonRef.current.clientHeight}px) 0 0)`;

      // If the textbox contains more lines than the specified number, show the read more button
      // and limit the container height based on open/closed state. Otherwise, if the text is too
      // short to require a read more button, hide the button
      if (numLines > lines) {
        buttonContainerRef.current.style.display = 'flex';

        if (isOpen) {
          mainContainerRef.current.style.height = '';
        } else {
          mainContainerRef.current.style.height = `${lines * lineHeight}px`;
        }
      } else {
        buttonContainerRef.current.style.display = 'none';
      }
    }
  }, [
    mainContainerRef,
    textContainerRef,
    buttonContainerRef,
    buttonRef,
    width,
    isOpen,
    lines,
    children,
  ]);

  /**
   * When the provided button text is a string, parse it as HTML, as it probably came from the
   * `data-read-more-text` or `data-read-less-text` attributes. Otherwise, use the provided JSX
   * element.
   */
  const closeText = useMemo(() => {
    return typeof readMoreText === 'string'
      ? <HTMLReactParserSafe html={readMoreText} />
      : readMoreText;
  }, [readMoreText]);

  const openText = useMemo(() => {
    return typeof readLessText === 'string'
      ? <HTMLReactParserSafe html={readLessText} />
      : readLessText;
  }, [readLessText]);

  /**
   * When the provided text is a string, parse it as HTML, as it probably came from the innerHTML
   * property via ReactDOM. Otherwise, use the provided JSX element.
   */
  const text = useMemo(() => {
    return typeof children === 'string'
      ? <HTMLReactParserSafe html={children} />
      : children;
  }, [children]);

  return (
    <div className={`auto-read-more ${className}`}>
      <div className="read-more-container" ref={mainContainerRef}>
        <div className="read-more-button-container" ref={buttonContainerRef}>
          <button
            className="read-more-button"
            type="button"
            onClick={() => setIsOpen((o) => !o)}
            ref={buttonRef}
          >
            {isOpen ? openText : closeText}
          </button>
        </div>
        <div className="read-more-text-container" ref={textContainerRef}>
          {text}
        </div>
      </div>
    </div>
  );
};

export default AutoReadMore;

document.querySelectorAll('.auto-read-more-react').forEach((readMoreElement) => {
  const lines = JSON.parse(readMoreElement.getAttribute('data-lines') ?? '3');
  const text = readMoreElement.innerHTML;
  const readMoreText = unescape(readMoreElement.getAttribute('data-read-more-text') ?? undefined) || undefined;
  const readLessText = unescape(readMoreElement.getAttribute('data-read-less-text') ?? undefined) || undefined;

  ReactDOM.render(
    <AutoReadMore
      lines={lines}
      readMoreText={readMoreText}
      readLessText={readLessText}
    >
      {text}
    </AutoReadMore>,
    readMoreElement,
  );
});
